/**
 * websocket.fallback.ts — SharedWorker 降级入口（主线程"假 Worker"）
 *
 * 当浏览器不支持 SharedWorker 时，在主线程运行，替代 ws-worker.js。
 *
 * 与 websocket.worker.ts 的接口完全一致：
 * - 接收 MessagePort，通过 port.onmessage 处理 connect/send 命令
 * - 通过 port.postMessage 广播 connected/disconnected/kicked/message 事件
 *
 * 区别：
 *   1. 运行在主线程（通过 MessageChannel 注入 port，而非 SharedWorker 的 self.onconnect）
 *   2. 不共享 WebSocket 连接（每个 tab 独立连接，技术上无法跨 tab 共享）
 *
 * 通信协议（与 websocket.worker.ts 一致）：
 *   Service → Fallback：{ cmd: 'connect', wsUrl: string } | { cmd: 'send', type: string, data: object } | { cmd: 'disconnect' }
 *   Fallback → Service：{ event: 'connected', isReconnect: boolean }
 *                      | { event: 'disconnected' }
 *                      | { event: 'kicked' }
 *                      | { event: 'authFailed' }（认证失败，已停止重连）
 *                      | { event: 'message', type: string, data: unknown }
 */
import { createWsManager } from './websocket.core';

/**
 * 初始化降级 Worker。
 *
 * @param port 由主线程的 MessageChannel.port2 传入——"假 Worker"通过此 port 与 Service 通信，
 *             行为等同于 SharedWorker 的 MessagePort。
 */
export function initFallbackWorker(port: MessagePort): void {
  // 降级场景只有唯一主线程，"广播 = 发回给唯一的 port"
  const manager = createWsManager({
    onBroadcast: (msg: object) => {
      port.postMessage(msg);
    },
  });

  port.onmessage = (ev: MessageEvent) => {
    const cmd = ev.data as
      | { cmd: 'connect'; wsUrl: string }
      | { cmd: 'send'; type: string; data: object }
      | { cmd: 'disconnect' };

    if (cmd.cmd === 'connect') {
      manager.connect(cmd.wsUrl);
    } else if (cmd.cmd === 'send') {
      manager.send(cmd.type, cmd.data);
    } else if (cmd.cmd === 'disconnect') {
      manager.destroy();
    }
  };
}
